/**
 * API client functions for the Configuration Service.
 * Handles servers, service instances, environments,
 * artifacts, and other configuration entities.
 */

import { apiClient } from '@/modules/api/api-client';

// --- Types ---

export interface Server {
  id: string;
  environmentId: string;
  projectKey: string;
  name: string;
  inventoryName?: string;
  type?: string;
  awsInstanceId?: string;
  awsRegion?: string;
  awsProfile?: string;
  // Ansible connection fields
  ansibleHost?: string;
  ansibleUser?: string;
  ansibleOs?: string;
  ansiblePort?: number;
  ansibleBecomeMethod?: string;
  sshKeyRef?: string;
  ansiblePassword?: string;
  // WinRM fields (applicable when ansibleOs === 'windows')
  ansibleWinrmScheme?: string;
  ansibleWinrmTransport?: string;
  ansibleWinrmCertValidation?: string;
}

export interface ServiceInstance {
  id: string;
  serviceDefinitionId: string;
  serverId: string;
  environmentId: string;
  projectKey: string;
  serviceName?: string;
  host?: string;
  port?: number;
}

export interface EnvironmentSummary {
  id: string;
  name: string;
  projectKey: string;
  priority: number;
  // Optional AWS cross-account access
  awsAccountId?: string;
  awsAssumeRoleArn?: string;
  awsExternalId?: string;
  awsDefaultRegion?: string;
  awsAuthType?: 'none' | 'role' | 'static';
  awsAccessKeyId?: string;
  awsSecretAccessKey?: string;
  awsProfileName?: string;
}
// --- Environment APIs ---

export async function listEnvironments(projectKey: string): Promise<EnvironmentSummary[]> {
  return apiClient.get<EnvironmentSummary[]>(
    `/projects/${encodeURIComponent(projectKey)}/environments`
  );
}

// --- Server APIs (environment-scoped) ---

export async function listServers(
  projectKey: string,
  environmentId: string
): Promise<Server[]> {
  return apiClient.get<Server[]>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/servers`
  );
}

export async function createServer(
  projectKey: string,
  environmentId: string,
  server: Omit<Server, 'id' | 'environmentId' | 'projectKey'>
): Promise<Server> {
  return apiClient.post<Server>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/servers`,
    server
  );
}

export async function updateServer(
  projectKey: string,
  environmentId: string,
  serverId: string,
  server: Partial<Server>
): Promise<Server> {
  return apiClient.put<Server>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/servers/${encodeURIComponent(serverId)}`,
    server
  );
}

export async function deleteServer(
  projectKey: string,
  environmentId: string,
  serverId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/servers/${encodeURIComponent(serverId)}`
  );
}

// --- Service Instance APIs (environment-scoped) ---

export async function listServiceInstances(
  projectKey: string,
  environmentId: string
): Promise<ServiceInstance[]> {
  return apiClient.get<ServiceInstance[]>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/service-instances`
  );
}

export async function createServiceInstance(
  projectKey: string,
  environmentId: string,
  instance: Omit<ServiceInstance, 'id' | 'environmentId' | 'projectKey'>
): Promise<ServiceInstance> {
  return apiClient.post<ServiceInstance>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/service-instances`,
    instance
  );
}

export async function updateServiceInstance(
  projectKey: string,
  environmentId: string,
  instanceId: string,
  instance: Partial<ServiceInstance>
): Promise<ServiceInstance> {
  return apiClient.put<ServiceInstance>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/service-instances/${encodeURIComponent(instanceId)}`,
    instance
  );
}

export async function deleteServiceInstance(
  projectKey: string,
  environmentId: string,
  instanceId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/service-instances/${encodeURIComponent(instanceId)}`
  );
}

// --- Environment CRUD APIs ---

export interface CreateEnvironmentRequest {
  id: string;
  name: string;
  priority: number;
  url?: string;
  // Optional AWS cross-account access
  awsAccountId?: string;
  awsAssumeRoleArn?: string;
  awsExternalId?: string;
  awsDefaultRegion?: string;
  awsAuthType?: 'none' | 'role' | 'static';
  awsAccessKeyId?: string;
  awsSecretAccessKey?: string;
  awsProfileName?: string;
}

export async function createEnvironment(
  projectKey: string,
  environment: CreateEnvironmentRequest
): Promise<EnvironmentSummary> {
  return apiClient.post<EnvironmentSummary>(
    `/projects/${encodeURIComponent(projectKey)}/environments`,
    { ...environment, projectKey }
  );
}

export async function updateEnvironment(
  projectKey: string,
  environmentId: string,
  environment: Partial<CreateEnvironmentRequest>
): Promise<EnvironmentSummary> {
  return apiClient.put<EnvironmentSummary>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}`,
    { ...environment, id: environmentId, projectKey }
  );
}

export async function deleteEnvironment(
  projectKey: string,
  environmentId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}`
  );
}
// --- Service Definition Types & APIs (project-scoped) ---

export interface ServiceDefinition {
  id: string;
  projectKey: string;
  name: string;
  port?: number;
  type?: string;
  serviceName?: string;
}

export async function listServiceDefinitions(projectKey: string): Promise<ServiceDefinition[]> {
  return apiClient.get<ServiceDefinition[]>(
    `/projects/${encodeURIComponent(projectKey)}/services`
  );
}

export async function createServiceDefinition(
  projectKey: string,
  service: Omit<ServiceDefinition, 'id' | 'projectKey'>
): Promise<ServiceDefinition> {
  return apiClient.post<ServiceDefinition>(
    `/projects/${encodeURIComponent(projectKey)}/services`,
    service
  );
}

export async function updateServiceDefinition(
  projectKey: string,
  serviceId: string,
  service: Omit<ServiceDefinition, 'id' | 'projectKey'>
): Promise<ServiceDefinition> {
  return apiClient.put<ServiceDefinition>(
    `/projects/${encodeURIComponent(projectKey)}/services/${encodeURIComponent(serviceId)}`,
    service
  );
}

export async function deleteServiceDefinition(
  projectKey: string,
  serviceId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/services/${encodeURIComponent(serviceId)}`
  );
}

// --- Artifact Types & APIs (project-scoped) ---

export interface Artifact {
  id: string;
  projectKey: string;
  name: string;
  artifactGroup?: string;
  artifactName?: string;
  artifactPackaging?: string;
  downloadType?: 'maven' | 'npm' | 'pypi';
  artifactRepository?: string;
  classifier?: string;
  // Deployment default fields
  deployable: boolean;
  defaultDeploymentType?: string;
  defaultDeployPath?: string;
  defaultDownloadPath?: string;
  defaultPort?: number;
  defaultOwnerGroup?: string;
  defaultOwnerName?: string;
  defaultServiceDefinitionId?: string;
  defaultContextPath?: string;
}

// --- Resolved Deployment Configuration ---

export interface ResolvedField<T> {
  value: T | null;
  source: 'default' | 'override';
}

export interface ResolvedDeploymentConfig {
  deployPath: ResolvedField<string>;
  downloadPath: ResolvedField<string>;
  port: ResolvedField<number>;
  ownerGroup: ResolvedField<string>;
  ownerName: ResolvedField<string>;
  serviceDefinitionId: ResolvedField<string>;
  contextPath: ResolvedField<string>;
}

export interface DeploymentArtifact {
  id: string;
  projectKey: string;
  artifactId: string;
  environmentId: string;
  deploymentType?: string;
  deployPath?: string;
  downloadPath?: string;
  port?: number;
  ownerGroup?: string;
  ownerName?: string;
  serviceDefinitionId?: string;
  contextPath?: string;
}

export interface PackageVersion {
  version: string;
  status: string;
}

export interface PackageAsset {
  name: string;
  size: number;
}

export interface DownloadUrlResponse {
  url: string;
  expiresAt: string;
}

export async function listArtifacts(projectKey: string): Promise<Artifact[]> {
  return apiClient.get<Artifact[]>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts`
  );
}

export async function createArtifact(
  projectKey: string,
  artifact: Omit<Artifact, 'id' | 'projectKey'>
): Promise<Artifact> {
  return apiClient.post<Artifact>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts`,
    artifact
  );
}

export async function deleteArtifact(
  projectKey: string,
  artifactId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}`
  );
}

export async function updateArtifact(
  projectKey: string,
  artifactId: string,
  artifact: Omit<Artifact, 'id' | 'projectKey'>
): Promise<Artifact> {
  return apiClient.put<Artifact>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}`,
    artifact
  );
}

/**
 * Performs a PATCH-style partial update on an artifact by fetching the current
 * state, merging in the provided partial fields, and sending the merged payload
 * via the existing PUT endpoint.
 *
 * Useful for toggling the `deployable` flag or updating individual default fields
 * without needing to supply the full artifact payload.
 */
export async function updateArtifactPartial(
  projectKey: string,
  artifactId: string,
  partial: Partial<Omit<Artifact, 'id' | 'projectKey'>>
): Promise<Artifact> {
  // Fetch current artifact state
  const artifacts = await listArtifacts(projectKey);
  const current = artifacts.find((a) => a.id === artifactId);
  if (!current) {
    throw new Error(`Artifact with id "${artifactId}" not found in project "${projectKey}"`);
  }

  // Merge partial payload into the current artifact (excluding id and projectKey)
  const { id: _id, projectKey: _pk, ...currentFields } = current;
  const merged: Omit<Artifact, 'id' | 'projectKey'> = {
    ...currentFields,
    ...partial,
  };

  return updateArtifact(projectKey, artifactId, merged);
}

// --- Deployment Artifact APIs (artifact-scoped) ---

export async function listDeploymentArtifacts(
  projectKey: string,
  artifactId: string
): Promise<DeploymentArtifact[]> {
  return apiClient.get<DeploymentArtifact[]>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}/deployment-artifacts`
  );
}

export async function createDeploymentArtifact(
  projectKey: string,
  artifactId: string,
  deploymentArtifact: Omit<DeploymentArtifact, 'id' | 'projectKey' | 'artifactId'>
): Promise<DeploymentArtifact> {
  return apiClient.post<DeploymentArtifact>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}/deployment-artifacts`,
    deploymentArtifact
  );
}

export async function updateDeploymentArtifact(
  projectKey: string,
  artifactId: string,
  deploymentArtifactId: string,
  deploymentArtifact: Partial<DeploymentArtifact>
): Promise<DeploymentArtifact> {
  return apiClient.put<DeploymentArtifact>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}/deployment-artifacts/${encodeURIComponent(deploymentArtifactId)}`,
    deploymentArtifact
  );
}

export async function deleteDeploymentArtifact(
  projectKey: string,
  artifactId: string,
  deploymentArtifactId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}/deployment-artifacts/${encodeURIComponent(deploymentArtifactId)}`
  );
}

// --- Resolved Deployment API ---

export async function getResolvedDeployment(
  projectKey: string,
  artifactId: string,
  environmentId: string
): Promise<ResolvedDeploymentConfig> {
  return apiClient.get<ResolvedDeploymentConfig>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}/resolved-deployment?environmentId=${encodeURIComponent(environmentId)}`
  );
}

/**
 * Batch: returns all deployment artifacts for a project in a single request.
 */
export async function listAllDeploymentArtifacts(
  projectKey: string
): Promise<DeploymentArtifact[]> {
  return apiClient.get<DeploymentArtifact[]>(
    `/projects/${encodeURIComponent(projectKey)}/deployment-artifacts`
  );
}

/**
 * Batch: returns resolved deployment configs for all deployable artifacts in one request.
 * The response is a map from artifact ID to ResolvedDeploymentConfig.
 * Artifacts with no resolvable config are omitted from the map.
 */
export async function getAllResolvedDeployments(
  projectKey: string,
  environmentId: string
): Promise<Record<string, ResolvedDeploymentConfig>> {
  return apiClient.get<Record<string, ResolvedDeploymentConfig>>(
    `/projects/${encodeURIComponent(projectKey)}/resolved-deployments?environmentId=${encodeURIComponent(environmentId)}`
  );
}

// --- CodeArtifact Version & Download APIs (artifact-scoped) ---

export async function listArtifactVersions(
  projectKey: string,
  artifactId: string
): Promise<PackageVersion[]> {
  return apiClient.get<PackageVersion[]>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}/versions`
  );
}

export async function listVersionAssets(
  projectKey: string,
  artifactId: string,
  version: string
): Promise<PackageAsset[]> {
  return apiClient.get<PackageAsset[]>(
    `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}/versions/${encodeURIComponent(version)}/assets`
  );
}

export async function getArtifactDownloadUrl(
  projectKey: string,
  artifactId: string,
  version: string,
  asset?: string
): Promise<DownloadUrlResponse> {
  const basePath = `/projects/${encodeURIComponent(projectKey)}/artifacts/${encodeURIComponent(artifactId)}/versions/${encodeURIComponent(version)}/download`;
  const url = asset ? `${basePath}?asset=${encodeURIComponent(asset)}` : basePath;
  return apiClient.get<DownloadUrlResponse>(url);
}

// --- Configuration File Types & APIs (project-scoped) ---

export interface ConfigurationFile {
  id: string;
  projectKey: string;
  name: string;
  defaultSourceRoot?: string;
  sourceLocation?: string;
  sourceFilename?: string;
  targetLocation?: string;
  targetFilename?: string;
  ownerGroup?: string;
  ownerName?: string;
  type?: string;
  serviceDefinitionId?: string;
}

export async function listConfigFiles(projectKey: string): Promise<ConfigurationFile[]> {
  return apiClient.get<ConfigurationFile[]>(
    `/projects/${encodeURIComponent(projectKey)}/config-files`
  );
}

export async function createConfigFile(
  projectKey: string,
  configFile: Omit<ConfigurationFile, 'id' | 'projectKey'>
): Promise<ConfigurationFile> {
  return apiClient.post<ConfigurationFile>(
    `/projects/${encodeURIComponent(projectKey)}/config-files`,
    configFile
  );
}

export async function updateConfigFile(
  projectKey: string,
  configFileId: string,
  configFile: Omit<ConfigurationFile, 'id' | 'projectKey'>
): Promise<ConfigurationFile> {
  return apiClient.put<ConfigurationFile>(
    `/projects/${encodeURIComponent(projectKey)}/config-files/${encodeURIComponent(configFileId)}`,
    configFile
  );
}

export async function deleteConfigFile(
  projectKey: string,
  configFileId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/config-files/${encodeURIComponent(configFileId)}`
  );
}

// --- Configuration File Override Types & APIs ---

export interface ConfigFileOverride {
  id: string;
  configFileId: string;
  environmentId: string;
  projectKey: string;
  sourceRoot?: string;
  targetLocation?: string;
  targetFilename?: string;
}

export async function listConfigFileOverrides(
  projectKey: string,
  configFileId: string
): Promise<ConfigFileOverride[]> {
  return apiClient.get<ConfigFileOverride[]>(
    `/projects/${encodeURIComponent(projectKey)}/config-files/${encodeURIComponent(configFileId)}/overrides`
  );
}

export async function createConfigFileOverride(
  projectKey: string,
  configFileId: string,
  override: Omit<ConfigFileOverride, 'id' | 'configFileId' | 'projectKey'>
): Promise<ConfigFileOverride> {
  return apiClient.post<ConfigFileOverride>(
    `/projects/${encodeURIComponent(projectKey)}/config-files/${encodeURIComponent(configFileId)}/overrides`,
    override
  );
}

export async function updateConfigFileOverride(
  projectKey: string,
  configFileId: string,
  overrideId: string,
  override: Partial<ConfigFileOverride>
): Promise<ConfigFileOverride> {
  return apiClient.put<ConfigFileOverride>(
    `/projects/${encodeURIComponent(projectKey)}/config-files/${encodeURIComponent(configFileId)}/overrides/${encodeURIComponent(overrideId)}`,
    override
  );
}

export async function deleteConfigFileOverride(
  projectKey: string,
  configFileId: string,
  overrideId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/config-files/${encodeURIComponent(configFileId)}/overrides/${encodeURIComponent(overrideId)}`
  );
}

// --- Configuration Version API ---

/**
 * Lists available config versions by reading the `releases/` directory
 * in the Config Git Repository. Returns directory names (e.g. ["v2.1.0", "v2.0.0"]).
 * The "current" option is NOT included in the response — it is prepended by the frontend.
 */
export async function listConfigVersions(
  projectKey: string
): Promise<string[]> {
  return apiClient.get<string[]>(
    `/projects/${encodeURIComponent(projectKey)}/config-versions`
  );
}

// --- Playbook Types & APIs (project-scoped) ---

export type PlaybookType =
  | 'DEPLOY'
  | 'CONFIGURATION'
  | 'RUN_DB_SCRIPT'
  | 'SERVICE_ACTION'
  | 'SERVICE_STATUS'
  | 'UTILITY'
  | 'SYSTEM_BUILD'
  | 'SERVER_UP'
  | 'REBOOT'
  | 'NONE';

export interface AnsibleVariable {
  key: string;
  value: string;
}

export interface Playbook {
  id?: number;
  projectKey: string;
  name: string;
  playbookType: PlaybookType;
  location: string;
  definitionId?: number | null;
  defaultVars?: AnsibleVariable[];
}

export interface PlaybookDefinition {
  id: number;
  name: string;
  description: string | null;
  path: string;
  playbookType: PlaybookType;
  createdTimestamp: string | null;
}

export interface PlaybookDefinitionRequest {
  name: string;
  description: string | null;
  path: string;
  playbookType: PlaybookType;
}

export async function listPlaybooks(projectKey: string): Promise<Playbook[]> {
  return apiClient.get<Playbook[]>(
    `/projects/${encodeURIComponent(projectKey)}/playbooks`
  );
}

export async function createPlaybook(
  projectKey: string,
  playbook: Omit<Playbook, 'id' | 'projectKey'>
): Promise<Playbook> {
  return apiClient.post<Playbook>(
    `/projects/${encodeURIComponent(projectKey)}/playbooks`,
    { ...playbook, id: 0, projectKey }
  );
}

export async function updatePlaybook(
  projectKey: string,
  playbookId: number,
  playbook: Omit<Playbook, 'id' | 'projectKey'>
): Promise<Playbook> {
  return apiClient.put<Playbook>(
    `/projects/${encodeURIComponent(projectKey)}/playbooks/${encodeURIComponent(playbookId)}`,
    { ...playbook, id: playbookId, projectKey }
  );
}

export async function deletePlaybook(
  projectKey: string,
  playbookId: number
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/playbooks/${encodeURIComponent(playbookId)}`
  );
}

// --- Playbook Definition APIs (global, not project-scoped) ---

export async function listPlaybookDefinitions(playbookType?: PlaybookType): Promise<PlaybookDefinition[]> {
  const path = playbookType
    ? `/playbook-definitions?playbookType=${encodeURIComponent(playbookType)}`
    : '/playbook-definitions';
  return apiClient.get<PlaybookDefinition[]>(path);
}

export async function getPlaybookDefinition(id: number): Promise<PlaybookDefinition> {
  return apiClient.get<PlaybookDefinition>(`/playbook-definitions/${encodeURIComponent(id)}`);
}

export async function createPlaybookDefinition(request: PlaybookDefinitionRequest): Promise<PlaybookDefinition> {
  return apiClient.post<PlaybookDefinition>('/playbook-definitions', request);
}

export async function updatePlaybookDefinition(id: number, request: PlaybookDefinitionRequest): Promise<PlaybookDefinition> {
  return apiClient.put<PlaybookDefinition>(`/playbook-definitions/${encodeURIComponent(id)}`, request);
}

export async function deletePlaybookDefinition(id: number): Promise<void> {
  return apiClient.delete(`/playbook-definitions/${encodeURIComponent(id)}`);
}

// --- Project Default Variables APIs ---

export async function listProjectDefaultVars(projectKey: string): Promise<AnsibleVariable[]> {
  return apiClient.get<AnsibleVariable[]>(
    `/projects/${encodeURIComponent(projectKey)}/default-vars`
  );
}

export async function saveProjectDefaultVars(
  projectKey: string,
  vars: AnsibleVariable[]
): Promise<AnsibleVariable[]> {
  return apiClient.put<AnsibleVariable[]>(
    `/projects/${encodeURIComponent(projectKey)}/default-vars`,
    vars
  );
}

export async function deleteProjectDefaultVar(
  projectKey: string,
  varKey: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/default-vars/${encodeURIComponent(varKey)}`
  );
}

// --- Deployed Artifact History ---

export interface DeployedArtifact {
  id: number;
  artifactId: string;
  environmentId: string;
  projectKey: string;
  version: string;
  deployedAt: string;
}

export async function listDeployedArtifactHistory(
  projectKey: string,
  environmentId: string,
  artifactId: string
): Promise<DeployedArtifact[]> {
  return apiClient.get<DeployedArtifact[]>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/deployed-artifacts?artifactId=${encodeURIComponent(artifactId)}`
  );
}

// --- Deployed Config File History (audit records) ---

export interface DeployedConfigFileRecord {
  id: number;
  configFileId: string;
  environmentId: string;
  projectKey: string;
  version: string;
  deployedAt: string;
}

/**
 * Lists deployed config file records for an environment.
 * If configFileId is provided, returns the full history for that file.
 * Otherwise, returns the latest deployment per config file in the environment.
 */
export async function listDeployedConfigFiles(
  projectKey: string,
  environmentId: string,
  configFileId?: string
): Promise<DeployedConfigFileRecord[]> {
  const params = configFileId ? `?configFileId=${encodeURIComponent(configFileId)}` : '';
  return apiClient.get<DeployedConfigFileRecord[]>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/deployed-config-files${params}`
  );
}

// --- Changed Service History (audit records) ---

export interface ChangedServiceRecord {
  id: number;
  serviceInstanceId: string;
  environmentId: string;
  projectKey: string;
  lastAction: string;
  changedAt: string;
}

/**
 * Lists changed service records for an environment.
 * If serviceInstanceId is provided, returns the full history for that service.
 * Otherwise, returns the latest state change per service instance in the environment.
 */
export async function listChangedServices(
  projectKey: string,
  environmentId: string,
  serviceInstanceId?: string
): Promise<ChangedServiceRecord[]> {
  const params = serviceInstanceId ? `?serviceInstanceId=${encodeURIComponent(serviceInstanceId)}` : '';
  return apiClient.get<ChangedServiceRecord[]>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/changed-services${params}`
  );
}

// --- Changed Database History (audit records) ---

export interface ChangedDatabase {
  id: number;
  databaseId: string;
  environmentId: string;
  projectKey: string;
  scriptName: string;
  executedAt: string;
}

export async function listChangedDatabases(
  projectKey: string,
  databaseId: string,
  environmentId?: string
): Promise<ChangedDatabase[]> {
  if (!environmentId) {
    return [];
  }
  return apiClient.get<ChangedDatabase[]>(
    `/projects/${encodeURIComponent(projectKey)}/environments/${encodeURIComponent(environmentId)}/changed-databases?databaseId=${encodeURIComponent(databaseId)}`
  );
}


// --- Database Definition Types & APIs (project-scoped) ---

export interface DatabaseDefinition {
  id: string;
  projectKey: string;
  name: string;
  port?: number;
  databaseName?: string;
  username?: string;
  password?: string;
  type?: 'mysql' | 'postgresql' | 'oracle' | 'mssql';
  patchScriptPath?: string;
}

export async function listDatabaseDefinitions(projectKey: string): Promise<DatabaseDefinition[]> {
  return apiClient.get<DatabaseDefinition[]>(
    `/projects/${encodeURIComponent(projectKey)}/databases`
  );
}

export async function createDatabaseDefinition(
  projectKey: string,
  definition: Omit<DatabaseDefinition, 'id' | 'projectKey'>
): Promise<DatabaseDefinition> {
  return apiClient.post<DatabaseDefinition>(
    `/projects/${encodeURIComponent(projectKey)}/databases`,
    definition
  );
}

export async function updateDatabaseDefinition(
  projectKey: string,
  databaseId: string,
  definition: Omit<DatabaseDefinition, 'id' | 'projectKey'>
): Promise<DatabaseDefinition> {
  return apiClient.put<DatabaseDefinition>(
    `/projects/${encodeURIComponent(projectKey)}/databases/${encodeURIComponent(databaseId)}`,
    definition
  );
}

export async function deleteDatabaseDefinition(
  projectKey: string,
  databaseId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/databases/${encodeURIComponent(databaseId)}`
  );
}

// --- Database Environment Config Types & APIs ---

export interface DatabaseEnvironmentConfig {
  id: string;
  databaseDefinitionId: string;
  environmentId: string;
  projectKey: string;
  host?: string;
  port?: number;
  databaseName?: string;
  username?: string;
  password?: string;
  sshTunnelServerId?: string;
}

export async function listDatabaseEnvironmentConfigs(
  projectKey: string,
  databaseId: string
): Promise<DatabaseEnvironmentConfig[]> {
  return apiClient.get<DatabaseEnvironmentConfig[]>(
    `/projects/${encodeURIComponent(projectKey)}/databases/${encodeURIComponent(databaseId)}/environment-configs`
  );
}

export async function getDatabaseEnvironmentConfig(
  projectKey: string,
  databaseId: string,
  configId: string
): Promise<DatabaseEnvironmentConfig> {
  return apiClient.get<DatabaseEnvironmentConfig>(
    `/projects/${encodeURIComponent(projectKey)}/databases/${encodeURIComponent(databaseId)}/environment-configs/${encodeURIComponent(configId)}`
  );
}

export async function createDatabaseEnvironmentConfig(
  projectKey: string,
  databaseId: string,
  config: Partial<DatabaseEnvironmentConfig>
): Promise<DatabaseEnvironmentConfig> {
  return apiClient.post<DatabaseEnvironmentConfig>(
    `/projects/${encodeURIComponent(projectKey)}/databases/${encodeURIComponent(databaseId)}/environment-configs`,
    config
  );
}

export async function updateDatabaseEnvironmentConfig(
  projectKey: string,
  databaseId: string,
  configId: string,
  config: Partial<DatabaseEnvironmentConfig>
): Promise<DatabaseEnvironmentConfig> {
  return apiClient.put<DatabaseEnvironmentConfig>(
    `/projects/${encodeURIComponent(projectKey)}/databases/${encodeURIComponent(databaseId)}/environment-configs/${encodeURIComponent(configId)}`,
    config
  );
}

export async function deleteDatabaseEnvironmentConfig(
  projectKey: string,
  databaseId: string,
  configId: string
): Promise<void> {
  return apiClient.delete(
    `/projects/${encodeURIComponent(projectKey)}/databases/${encodeURIComponent(databaseId)}/environment-configs/${encodeURIComponent(configId)}`
  );
}

// --- Database Script Listing API ---

export async function listDatabaseScripts(
  projectKey: string,
  databaseId: string
): Promise<string[]> {
  return apiClient.get<string[]>(
    `/projects/${encodeURIComponent(projectKey)}/databases/${encodeURIComponent(databaseId)}/scripts`
  );
}
